import React, { useState, useCallback, useRef } from 'react';
import {
  Show,
  SimpleShowLayout,
  TextField,
  DateField,
  NumberField,
  BooleanField,
  FunctionField,
  useRecordContext,
  useTranslate,
  TopToolbar,
  EditButton,
  DeleteButton,
  useDataProvider,
  useNotify,
  useRefresh,
} from 'react-admin';
import { apiClient } from '../../utils/apiClient';
import { API_CONFIG } from '../../config/api';
import { NumberField as NumberFieldInput } from '../common/NumberField';
import {
  Box,
  Typography,
  Chip,
  Card,
  CardContent,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField as MuiTextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  TablePagination,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  LocalOffer,
  Settings,
  People,
  Schedule,
  AttachMoney,
  LocationOn,
  ArrowBack,
  TrendingUp,
  Block,
  CheckCircle,
  Cancel,
  Event,
  AccessTime,
  BarChart,
  Add,
  ContentCopy,
  Download,
  Gavel,
  Translate,
} from '@mui/icons-material';

// Unique Coupon Codes Management Section
const UniqueCouponCodesSection = ({ promotionId }: { promotionId: string }) => {
  const record = useRecordContext();
  const [open, setOpen] = useState(false);
  const [quantity, setQuantity] = useState(10);
  const [batchName, setBatchName] = useState('');
  const [codeLength, setCodeLength] = useState(12);
  const [separator, setSeparator] = useState('-');
  const [segmentLength, setSegmentLength] = useState(4);
  const [codes, setCodes] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [generatingOpen, setGeneratingOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [totalAvailable, setTotalAvailable] = useState(0);
  const [totalRedeemed, setTotalRedeemed] = useState(0);
  const [orderBy, setOrderBy] = useState<string>('generated_at');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const translate = useTranslate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Track last loaded params to prevent duplicate API calls
  const lastLoadedParamsRef = useRef<string>('');

  // Extract loadCodes as a reusable function
  const loadCodes = useCallback(async () => {
    if (!promotionId) return;

    // Create a unique key for current params
    const currentParams = `${promotionId}-${page}-${rowsPerPage}-${searchQuery}`;

    // Skip if we just loaded with these exact same params
    if (lastLoadedParamsRef.current === currentParams) {
      return;
    }

    setLoading(true);
    try {
      const offset = page * rowsPerPage;
      let url = `/api/backend/promotion-codes?promotionId=${promotionId}&limit=${rowsPerPage}&offset=${offset}`;

      // Add search query if provided
      if (searchQuery.trim()) {
        url += `&search=${encodeURIComponent(searchQuery)}`;
      }

      const result: any = await apiClient.get(url);
      if (result.data) {
        setCodes(result.data);
        setTotal(result.total || 0);
        setTotalAvailable(result.totalAvailable || 0);
        setTotalRedeemed(result.totalRedeemed || 0);
      }

      // Update ref after successful load
      lastLoadedParamsRef.current = currentParams;
    } catch (error) {
      console.error('Error loading codes:', error);
      notify('Failed to load coupon codes', { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [promotionId, page, rowsPerPage, searchQuery, notify]);

  // Reload when promotionId, page, rowsPerPage, or searchQuery changes
  React.useEffect(() => {
    loadCodes();
  }, [loadCodes]);

  const handleGenerate = async () => {
    if (!batchName.trim()) {
      notify('Batch name is required', { type: 'error' });
      return;
    }

    setGeneratingOpen(true);

    try {
      // Use promotion's expiry date and ensure it's in correct ISO 8601 format with Z
      let expiresAt: string;
      if (record?.valid_until) {
        // Remove any existing timezone info and add Z for UTC
        const baseDate = record.valid_until.split('.')[0].split('+')[0].split('Z')[0];
        expiresAt = baseDate.includes('T') ? `${baseDate}Z` : `${baseDate}T23:59:59Z`;
      } else {
        // Default to 30 days from now
        expiresAt = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
      }

      const result: any = await apiClient.post('/api/backend/promotion-codes/generate', {
        promotionId,
        quantity,
        batchName,
        expiresAt,
        codeLength,
        separator,
        segmentLength,
      });

      if (result.success) {
        notify(`Successfully generated ${result.codesGenerated} coupon codes`, { type: 'success' });
        setGeneratingOpen(false);
        setOpen(false); // Close the dialog
        setBatchName('');
        setQuantity(10);
        setCodeLength(12);
        setSeparator('-');
        setSegmentLength(4);
        loadCodes();
        refresh();
      } else {
        throw new Error(result.error || 'Failed to generate codes');
      }
    } catch (error: any) {
      console.error('Error generating codes:', error);
      notify(error.message || 'Failed to generate coupon codes', { type: 'error' });
      setGeneratingOpen(false);
    }
  };

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    notify('Code copied to clipboard', { type: 'info' });
  };

  const handleExportCodes = () => {
    // Use backend export endpoint with full URL
    window.open(`${API_CONFIG.baseURL}/api/backend/promotion-codes/export?promotionId=${promotionId}`, '_blank');
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(0); // Reset to first page when searching
  };

  // Use backend-provided statistics
  // totalAvailable and totalRedeemed come from backend

  return (
    <Card sx={{ mb: 3, overflow: 'hidden' }}>
      <CardContent>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', mb: 3, gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <LocalOffer sx={{ mr: 2, color: '#00C853' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {translate('resources.promotions.show.uniqueCouponCodes')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={handleExportCodes}
              disabled={codes.length === 0}
              sx={{ textTransform: 'none' }}
            >
              {translate('resources.promotions.show.exportCsv')}
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setOpen(true)}
              sx={{
                backgroundColor: '#00C853',
                textTransform: 'none',
                '&:hover': { backgroundColor: '#00A843' },
              }}
            >
              {translate('resources.promotions.show.generateCodes')}
            </Button>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : codes.length === 0 ? (
          <Alert severity="info">
            {translate('resources.promotions.show.noCodes')}
          </Alert>
        ) : (
          <>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 3 }}>
              <Box sx={{ flex: 1, p: 2, backgroundColor: '#E8F5E9', borderRadius: 2 }}>
                <Typography variant="h4" color="success.main" sx={{ fontWeight: 700, fontSize: { xs: '1.25rem', sm: '2.125rem' } }}>
                  {totalAvailable}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {translate('resources.promotions.show.availableCodes')}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, p: 2, backgroundColor: '#FFF3E0', borderRadius: 2 }}>
                <Typography variant="h4" color="warning.main" sx={{ fontWeight: 700, fontSize: { xs: '1.25rem', sm: '2.125rem' } }}>
                  {totalRedeemed}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {translate('resources.promotions.show.redeemedCodes')}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, p: 2, backgroundColor: '#F5F5F5', borderRadius: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, fontSize: { xs: '1.25rem', sm: '2.125rem' } }}>
                  {total}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {translate('resources.promotions.show.totalCodes')}
                </Typography>
              </Box>
            </Box>

            {/* Search Input */}
            <MuiTextField
              fullWidth
              placeholder={translate('resources.promotions.show.searchPlaceholder')}
              value={searchQuery}
              onChange={handleSearchChange}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <Box sx={{ mr: 1, display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                    <Typography variant="body2">🔍</Typography>
                  </Box>
                ),
                ...(searchQuery && {
                  endAdornment: (
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearchQuery('');
                        setPage(0);
                      }}
                      sx={{ mr: -1 }}
                    >
                      <Cancel fontSize="small" />
                    </IconButton>
                  ),
                }),
              }}
            />

            {searchQuery && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Found {total} code(s) matching "{searchQuery}"
              </Alert>
            )}

            {isMobile ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {codes.map((code) => (
                  <Box key={code.id} sx={{ p: 1.5, border: '1px solid #E0E0E0', borderRadius: 2, overflow: 'hidden' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {code.code}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Chip
                          label={code.is_redeemed ? translate('resources.promotions.show.redeemed') : translate('resources.promotions.show.available')}
                          color={code.is_redeemed ? 'default' : 'success'}
                          size="small"
                        />
                        <IconButton size="small" onClick={() => handleCopyCode(code.code)} disabled={code.is_redeemed}>
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                    {code.is_redeemed && (
                      <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          {code.redeemed_at ? new Date(code.redeemed_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {code.redeemed_by || '-'}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>{translate('resources.promotions.show.code')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>{translate('resources.promotions.show.statusLabel')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>{translate('resources.promotions.show.redeemedAt')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>{translate('resources.promotions.show.redeemedBy')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>{translate('resources.promotions.show.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                          <CircularProgress size={32} />
                        </TableCell>
                      </TableRow>
                    ) : codes.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            {searchQuery ? `No codes found matching "${searchQuery}"` : 'No codes available'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      codes.map((code) => (
                        <TableRow key={code.id}>
                          <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                            {code.code}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={code.is_redeemed ? translate('resources.promotions.show.redeemed') : translate('resources.promotions.show.available')}
                              color={code.is_redeemed ? 'default' : 'success'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {code.redeemed_at
                              ? new Date(code.redeemed_at).toLocaleString('en-GB', {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })
                              : '-'}
                          </TableCell>
                          <TableCell>{code.redeemed_by || '-'}</TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => handleCopyCode(code.code)}
                              disabled={code.is_redeemed}
                            >
                              <ContentCopy fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Pagination */}
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={total}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage={translate('resources.promotions.show.rowsPerPage')}
              sx={{ borderTop: '1px solid #E0E0E0' }}
            />
          </>
        )}
      </CardContent>

      {/* Generate Codes Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{translate('resources.promotions.show.generateDialog.title')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            <MuiTextField
              label={translate('resources.promotions.show.generateDialog.batchName')}
              value={batchName}
              onChange={(e) => setBatchName(e.target.value)}
              fullWidth
              required
              helperText={translate('resources.promotions.show.generateDialog.batchNameHelper')}
            />

            <NumberFieldInput
              label={translate('resources.promotions.show.generateDialog.numberOfCodes')}
              value={quantity}
              onValueChange={(v) => setQuantity(v ?? 1)}
              integer
              fullWidth
              inputProps={{ min: 1, max: 10000 }}
              helperText={translate('resources.promotions.show.generateDialog.numberOfCodesHelper')}
            />

            <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 1 }}>
              {translate('resources.promotions.show.generateDialog.codeFormatOptions')}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <NumberFieldInput
                label={translate('resources.promotions.show.generateDialog.codeLength')}
                value={codeLength}
                onValueChange={(v) => setCodeLength(v ?? 12)}
                integer
                sx={{ flex: 1 }}
                inputProps={{ min: 6, max: 20 }}
                helperText={translate('resources.promotions.show.generateDialog.codeLengthHelper')}
              />

              <NumberFieldInput
                label={translate('resources.promotions.show.generateDialog.segmentLength')}
                value={segmentLength}
                onValueChange={(v) => setSegmentLength(v ?? 4)}
                integer
                sx={{ flex: 1 }}
                inputProps={{ min: 3, max: 6 }}
                helperText={translate('resources.promotions.show.generateDialog.segmentLengthHelper')}
              />

              <MuiTextField
                label={translate('resources.promotions.show.generateDialog.separator')}
                value={separator}
                onChange={(e) => setSeparator(e.target.value.slice(0, 1))}
                sx={{ flex: 1 }}
                inputProps={{ maxLength: 1 }}
                helperText={translate('resources.promotions.show.generateDialog.separatorHelper')}
              />
            </Box>

            <Alert severity="info" sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>{translate('resources.promotions.show.generateDialog.preview')}</strong>{' '}
                {translate('resources.promotions.show.generateDialog.previewText')}
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '1.1em' }}
              >
                {Array(Math.ceil(codeLength / segmentLength))
                  .fill('X')
                  .map((_, i) =>
                    'X'.repeat(Math.min(segmentLength, codeLength - i * segmentLength))
                  )
                  .join(separator || '')}
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                {translate('resources.promotions.show.generateDialog.expiresOn')}{' '}
                <strong>
                  {record?.valid_until
                    ? new Date(record.valid_until).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })
                    : translate('resources.promotions.show.generateDialog.thirtyDays')}
                </strong>
                {' '}
                {translate('resources.promotions.show.generateDialog.sameAsPromotion')}
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setOpen(false)} sx={{ textTransform: 'none' }}>
            {translate('resources.promotions.show.generateDialog.cancel')}
          </Button>
          <Button
            onClick={handleGenerate}
            variant="contained"
            disabled={generatingOpen || quantity < 1 || quantity > 10000 || !batchName.trim()}
            sx={{
              backgroundColor: '#00C853',
              textTransform: 'none',
              '&:hover': { backgroundColor: '#00A843' },
            }}
          >
            {generatingOpen ? (
              <CircularProgress size={24} />
            ) : (
              translate('resources.promotions.show.generateDialog.generateButton', { quantity })
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

// Type icon component
const TypeIcon = ({ type }: { type: string }) => {
  switch (type) {
    case 'coupon':
      return <LocalOffer fontSize="small" />;
    case 'time_limited':
      return <Event fontSize="small" />;
    default:
      return <LocalOffer fontSize="small" />;
  }
};

const ShowActions = () => (
  <TopToolbar>
    <EditButton />
    <DeleteButton />
  </TopToolbar>
);

const PageHeader = () => {
  const record = useRecordContext();
  const translate = useTranslate();

  if (!record) return null;

  const now = new Date();
  const validFrom = new Date(record.valid_from);
  const validUntil = new Date(record.valid_until);

  let status = 'inactive';
  let color: 'default' | 'success' | 'error' | 'warning' = 'default';
  let icon = <Cancel />;

  if (record.is_active) {
    if (now < validFrom) {
      status = 'scheduled';
      color = 'warning';
      icon = <Schedule />;
    } else if (now >= validFrom && now <= validUntil) {
      status = 'active';
      color = 'success';
      icon = <CheckCircle />;
    } else {
      status = 'expired';
      color = 'error';
      icon = <Cancel />;
    }
  }

  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      mb: 4,
    }}>
      <IconButton
        onClick={() => window.history.back()}
        size="large"
        sx={{
          backgroundColor: '#FFFFFF',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          '&:hover': {
            backgroundColor: '#F5F5F5',
            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
          },
        }}
        title="Back to Promotions"
      >
        <ArrowBack />
      </IconButton>
      <Box>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '1.25rem', sm: '2.125rem' }, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
          {record.name}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Chip
            icon={<TypeIcon type={record.promotion_type} />}
            label={translate(`resources.promotions.types.${record.promotion_type || 'time_limited'}`)}
            size="small"
            variant="outlined"
          />
          <Chip
            label={translate(`resources.promotions.status.${status}`)}
            color={color}
            size="small"
            icon={icon}
            sx={{ fontWeight: 600 }}
          />
          {record.code && (
            <Chip
              label={`Code: ${record.code}`}
              size="small"
              variant="outlined"
              sx={{ fontFamily: 'monospace' }}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

const PromotionStatistics = () => {
  const record = useRecordContext();
  const translate = useTranslate();

  if (!record) return null;

  const currentUses = record.current_uses || 0;
  const maxUses = record.max_uses_total;
  const usagePercent = maxUses ? Math.round((currentUses / maxUses) * 100) : 0;

  return (
    <Card sx={{ mb: 3, overflow: 'hidden' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <BarChart sx={{ mr: 2, color: '#00C853' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {translate('resources.promotions.statusInfo.title')}
          </Typography>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 3 }}>
          <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
            <Typography variant="h3" color="primary" sx={{ fontWeight: 700, fontSize: { xs: '1.5rem', sm: '2rem', md: '3rem' } }}>
              {currentUses}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {translate('resources.promotions.fields.currentUses')}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
            <Typography variant="h3" color="success.main" sx={{ fontWeight: 700, fontSize: { xs: '1.5rem', sm: '2rem', md: '3rem' } }}>
              {maxUses || '∞'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {translate('resources.promotions.fields.maxUsesTotal')}
            </Typography>
          </Box>
          {maxUses && (
            <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
              <Typography variant="h3" sx={{ fontWeight: 700, fontSize: { xs: '1.5rem', sm: '2rem', md: '3rem' } }}>
                {usagePercent}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Usage Rate
              </Typography>
            </Box>
          )}
          {record.max_uses_per_user && (
            <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
              <Typography variant="h3" color="warning.main" sx={{ fontWeight: 700, fontSize: { xs: '1.5rem', sm: '2rem', md: '3rem' } }}>
                {record.max_uses_per_user}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {translate('resources.promotions.fields.maxUsesPerUser')}
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export const PromotionsShow = () => {
  const translate = useTranslate();

  return (
    <Show
      actions={false}
      title={false}
      component="div"
      sx={{
        backgroundColor: 'transparent',
        '& .RaShow-main': {
          backgroundColor: 'transparent',
          boxShadow: 'none',
        },
      }}
    >
      <SimpleShowLayout
        sx={{
          '& .MuiPaper-root': {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', pb: { xs: 4, sm: 0 } }}>
          <PageHeader />
          <PromotionStatistics />

          {/* Basic Information */}
          <Card sx={{ mb: 3, overflow: 'hidden' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <LocalOffer sx={{ mr: 2, color: '#00C853' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {translate('resources.promotions.sections.basicInfo')}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {translate('resources.promotions.fields.name')}
                  </Typography>
                  <TextField source="name" />
                </Box>

                <FunctionField
                  render={(record: any) => {
                    if (record.description) {
                      return (
                        <Box>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {translate('resources.promotions.fields.description')}
                          </Typography>
                          <Typography variant="body1">{record.description}</Typography>
                        </Box>
                      );
                    }
                    return null;
                  }}
                />

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {translate('resources.promotions.fields.promotionType')}
                    </Typography>
                    <FunctionField
                      render={(record: any) => (
                        <Typography variant="body1">
                          {translate(`resources.promotions.types.${record.promotion_type || 'time_limited'}`)}
                        </Typography>
                      )}
                    />
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {translate('resources.promotions.fields.codeType')}
                    </Typography>
                    <FunctionField
                      render={(record: any) => (
                        <Typography variant="body1">
                          {translate(`resources.promotions.codeTypes.${record.code_type || 'none'}`)}
                        </Typography>
                      )}
                    />
                  </Box>
                </Box>

                <FunctionField
                  render={(record: any) => {
                    if (record.code) {
                      return (
                        <Box>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {translate('resources.promotions.fields.code')}
                          </Typography>
                          <Typography variant="h6" sx={{ fontFamily: 'monospace', color: '#00C853' }}>
                            {record.code}
                          </Typography>
                        </Box>
                      );
                    }
                    return null;
                  }}
                />
              </Box>
            </CardContent>
          </Card>

          {/* Unique Coupon Codes Management */}
          <FunctionField
            render={(record: any) => {
              if (record.code_type === 'unique') {
                return <UniqueCouponCodesSection promotionId={record.id} />;
              }
              return null;
            }}
          />

          {/* Discount Configuration */}
          <Card sx={{ mb: 3, overflow: 'hidden' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <AttachMoney sx={{ mr: 2, color: '#00C853' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {translate('resources.promotions.sections.discount')}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {translate('resources.promotions.fields.discountType')}
                  </Typography>
                  <FunctionField
                    render={(record: any) => (
                      <Typography variant="body1">
                        {translate(`resources.promotions.discountTypes.${record.discount_type || 'percentage'}`)}
                      </Typography>
                    )}
                  />
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {translate('resources.promotions.fields.discountValue')}
                  </Typography>
                  <FunctionField
                    render={(record: any) => {
                      const type = record.discount_type;
                      const value = record.discount_value;
                      let display = value;

                      if (type === 'percentage') {
                        display = `${value}%`;
                      } else if (type === 'fixed_amount' || type === 'free_credit') {
                        display = `HKD ${value}`;
                      }

                      return <Typography variant="h6" sx={{ color: '#00C853' }}>{display}</Typography>;
                    }}
                  />
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {translate('resources.promotions.fields.minChargeAmount')}
                  </Typography>
                  <FunctionField
                    render={(record: any) => (
                      <Typography variant="body1">
                        {record.min_charge_amount ? `HKD ${record.min_charge_amount}` : translate('resources.promotions.show.noMinimum')}
                      </Typography>
                    )}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Target Users */}
          <Card sx={{ mb: 3, overflow: 'hidden' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <People sx={{ mr: 2, color: '#00C853' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {translate('resources.promotions.sections.targeting')}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {translate('resources.promotions.fields.userType')}
                  </Typography>
                  <FunctionField
                    render={(record: any) => (
                      <Typography variant="body1">
                        {translate(`resources.promotions.userTypes.${record.user_type || 'all'}`)}
                      </Typography>
                    )}
                  />
                </Box>

                <FunctionField
                  render={(record: any) => {
                    if (record.membership_levels && record.membership_levels.length > 0) {
                      return (
                        <Box>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {translate('resources.promotions.fields.membershipLevels')}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {record.membership_levels.map((level: string) => (
                              <Chip
                                key={level}
                                label={translate(`resources.promotions.memberships.${level}`)}
                                size="small"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        </Box>
                      );
                    }
                    return (
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {translate('resources.promotions.fields.membershipLevels')}
                        </Typography>
                        <Typography variant="body1">{translate('resources.promotions.show.allMembershipLevels')}</Typography>
                      </Box>
                    );
                  }}
                />
              </Box>
            </CardContent>
          </Card>

          {/* Validity Period */}
          <Card sx={{ mb: 3, overflow: 'hidden' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Schedule sx={{ mr: 2, color: '#00C853' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {translate('resources.promotions.sections.validity')}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {translate('resources.promotions.fields.validFrom')}
                    </Typography>
                    <DateField source="valid_from" showTime />
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {translate('resources.promotions.fields.validUntil')}
                    </Typography>
                    <DateField source="valid_until" showTime />
                  </Box>
                </Box>

                <FunctionField
                  render={(record: any) => {
                    if (record.time_of_day_start && record.time_of_day_end) {
                      return (
                        <Box sx={{ display: 'flex', gap: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              {translate('resources.promotions.fields.timeOfDayStart')}
                            </Typography>
                            <Typography variant="body1">{record.time_of_day_start}</Typography>
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              {translate('resources.promotions.fields.timeOfDayEnd')}
                            </Typography>
                            <Typography variant="body1">{record.time_of_day_end}</Typography>
                          </Box>
                        </Box>
                      );
                    }
                    return null;
                  }}
                />

                <FunctionField
                  render={(record: any) => {
                    if (record.days_of_week && record.days_of_week.length > 0) {
                      const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
                      return (
                        <Box>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {translate('resources.promotions.fields.daysOfWeek')}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {record.days_of_week.map((day: number) => (
                              <Chip
                                key={day}
                                label={dayNames[day - 1]}
                                size="small"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        </Box>
                      );
                    }
                    return (
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {translate('resources.promotions.fields.daysOfWeek')}
                        </Typography>
                        <Typography variant="body1">{translate('resources.promotions.show.allDaysOfWeek')}</Typography>
                      </Box>
                    );
                  }}
                />
              </Box>
            </CardContent>
          </Card>

          {/* Restrictions */}
          <Card sx={{ mb: 3, overflow: 'hidden' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <LocationOn sx={{ mr: 2, color: '#00C853' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {translate('resources.promotions.sections.restrictions')}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FunctionField
                  render={(record: any) => {
                    if (record.sites && record.sites.length > 0) {
                      return (
                        <Box>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {translate('resources.promotions.fields.sites')}
                          </Typography>
                          <Typography variant="body1">
                            {record.sites.length} site(s) configured
                          </Typography>
                        </Box>
                      );
                    }
                    return (
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {translate('resources.promotions.fields.sites')}
                        </Typography>
                        <Typography variant="body1">{translate('resources.promotions.show.allSites')}</Typography>
                      </Box>
                    );
                  }}
                />

                <FunctionField
                  render={(record: any) => {
                    if (record.charger_types && record.charger_types.length > 0) {
                      return (
                        <Box>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {translate('resources.promotions.fields.chargerTypes')}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {record.charger_types.map((type: string) => (
                              <Chip
                                key={type}
                                label={type}
                                size="small"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        </Box>
                      );
                    }
                    return (
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {translate('resources.promotions.fields.chargerTypes')}
                        </Typography>
                        <Typography variant="body1">{translate('resources.promotions.show.allChargerTypes')}</Typography>
                      </Box>
                    );
                  }}
                />
              </Box>
            </CardContent>
          </Card>

          {/* Advanced Settings */}
          <Card sx={{ mb: 3, overflow: 'hidden' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Settings sx={{ mr: 2, color: '#00C853' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {translate('resources.promotions.sections.advanced')}
                </Typography>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 2 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {translate('resources.promotions.fields.priority')}
                  </Typography>
                  <NumberField source="priority" />
                </Box>

                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {translate('resources.promotions.fields.stackableWithCoupons')}
                  </Typography>
                  <BooleanField source="stackable_with_coupons" />
                </Box>

                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {translate('resources.promotions.fields.stackableWithPromotions')}
                  </Typography>
                  <BooleanField source="stackable_with_promotions" />
                </Box>

                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {translate('resources.promotions.fields.isActive')}
                  </Typography>
                  <BooleanField source="is_active" />
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Terms & Conditions */}
          <FunctionField
            render={(record: any) => {
              if (record.terms && record.terms.length > 0) {
                return (
                  <Card sx={{ mb: 3, overflow: 'hidden' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <Gavel sx={{ mr: 2, color: '#F59E0B' }} />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {translate('resources.promotions.show.termsAndConditions')}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {record.terms.map((term: any, index: number) => (
                          <Box
                            key={index}
                            sx={{
                              p: 2,
                              backgroundColor: '#FFFBEB',
                              borderRadius: 1,
                              border: '1px solid #FDE68A'
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                              <Box sx={{
                                minWidth: 28,
                                height: 28,
                                borderRadius: '50%',
                                backgroundColor: '#F59E0B',
                                color: '#FFF',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 'bold',
                                fontSize: 14
                              }}>
                                {index + 1}
                              </Box>

                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body1" sx={{ color: '#78350F', lineHeight: 1.6 }}>
                                  {term.termText}
                                </Typography>

                                {/* Show translations if available */}
                                {term.translations && term.translations.some((t: any) => t.termText) && (
                                  <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #FDE68A' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                      <Translate sx={{ fontSize: 16, color: '#92400E' }} />
                                      <Typography variant="caption" sx={{ color: '#92400E', fontWeight: 600 }}>
                                        Translations
                                      </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                      {term.translations.map((translation: any, tIndex: number) => {
                                        if (!translation.termText) return null;
                                        const langNames: any = {
                                          'en': 'English',
                                          'zh-TW': '繁體中文',
                                          'zh-CN': '简体中文'
                                        };
                                        return (
                                          <Box key={tIndex}>
                                            <Typography variant="caption" sx={{ color: '#92400E', fontWeight: 600 }}>
                                              {langNames[translation.language] || translation.language}:
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: '#B45309', ml: 1 }}>
                                              {translation.termText}
                                            </Typography>
                                          </Box>
                                        );
                                      })}
                                    </Box>
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                );
              }
              return null;
            }}
          />

          {/* Timeline */}
          <Card sx={{ mb: 3, overflow: 'hidden' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <AccessTime sx={{ mr: 2, color: '#00C853' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {translate('resources.promotions.show.timeline')}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {translate('resources.promotions.show.createdAt')}
                  </Typography>
                  <DateField source="created_at" showTime />
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {translate('resources.promotions.show.updatedAt')}
                  </Typography>
                  <DateField source="updated_at" showTime />
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {translate('resources.promotions.show.createdBy')}
                  </Typography>
                  <TextField source="created_by" />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </SimpleShowLayout>
    </Show>
  );
};
